import React from 'react';
import { FormSummary } from '@alinta-digital/asg';
import { FuelSite } from '../../types';
import {
  capitalizeFirstLetter,
  pushAnalytics,
  resetFormContextError,
} from '../../utils/utils';
import { SummaryProps } from '../../types';
import { SummaryContent } from '../SummaryContent';
import { FormContainerContext } from '../../contexts/FormContainerContext';
import { PaymentCardType } from '@alinta-digital/payment-form/types';

interface DebitDetailsSummaryProps extends SummaryProps {
  selectedElectricity: FuelSite[];
  selectedGas: FuelSite[];
  debiltDetails: {
    bankAccountName: string;
    bsbNumber: string;
    paymentMethod: string;
    bankAccountNumber: string;
    creditCard?: {
      expiryMonth: string;
      expiryYear: string;
      last4: string;
      cardName?: string;
      cardType?: PaymentCardType;
    };
  };
}

export const DebitDetailsSummaryCore: React.FunctionComponent<DebitDetailsSummaryProps> = ({
  selectedElectricity,
  selectedGas,
  debiltDetails,
  showSummaryFields,
  onEditClick,
}) => {
  const {
    values: formContextValues,
    dispatch: formContextDispatch,
  } = React.useContext(FormContainerContext);

  const summaryData = [...selectedElectricity]
    .concat([...selectedGas])
    .map((fuel: FuelSite) => {
      return {
        label: {
          value: `${capitalizeFirstLetter(fuel.fuelType)} account number`,
          dataTestId: 'selectedFuelSummary_label',
        },
        content: {
          value: fuel.authAccountNumber,
          dataTestId: 'selectedFuelSummary_content',
        },
      };
    });

  React.useEffect(() => {
    showSummaryFields && pushAnalytics('load.0.directdebitstep3.edit');
  }, []);

  const isBankTransfer = debiltDetails.paymentMethod === 'bankTransfer';

  const paymentMethod = {
    label: {
      value: 'Payment method',
      dataTestId: 'paymentMethodSummary_label',
    },
    content: {
      value: isBankTransfer ? 'Bank account' : 'Card account',
      dataTestId: 'paymentMethodSummary_content',
    },
  };

  if (isBankTransfer) {
    summaryData.push(
      {
        label: {
          value: 'BSB',
          dataTestId: 'bsbSummary_label',
        },
        content: {
          value: debiltDetails.bsbNumber,
          dataTestId: 'bsbSummary_content',
        },
      },
      {
        label: {
          value: 'Bank account name',
          dataTestId: 'bankAccountNameSummary_label',
        },
        content: {
          value: debiltDetails.bankAccountName,
          dataTestId: 'bankAccountNameSummary_content',
        },
      },
      paymentMethod,
      {
        label: {
          value: 'Bank account number',
          dataTestId: 'bankAccountNumberSummary_label',
        },
        content: {
          value: debiltDetails.bankAccountNumber,
          dataTestId: 'bankAccountNumberSummary_content',
        },
      }
    );
  } else {
    summaryData.push(
      {
        label: {
          value: 'Card number',
          dataTestId: 'creditCardNumberSummary_label',
        },
        content: {
          value: `${
            debiltDetails.creditCard?.cardType === 'amex'
              ? `**** ****** *${debiltDetails.creditCard?.last4}`
              : `**** **** **** ${debiltDetails.creditCard?.last4}`
          }`,
          dataTestId: 'creditCardNumberSummary_content',
        },
      },
      {
        label: {
          value: 'Name on card',
          dataTestId: 'creditCardNameSummary_label',
        },
        content: {
          value: `${debiltDetails.creditCard?.cardName}`,
          dataTestId: 'creditCardNameSummary_content',
        },
      },
      paymentMethod,
      {
        label: {
          value: 'Card expiry date',
          dataTestId: 'creditCardExpirySummary_label',
        },
        content: {
          value: `${
            debiltDetails.creditCard?.expiryMonth
          }/${debiltDetails.creditCard?.expiryYear?.substring(2)}`,
          dataTestId: 'creditCardExpirySummary_content',
        },
      }
    );
  }

  return (
    <FormSummary
      heading={`Step 3: ${isBankTransfer ? 'Bank details' : 'Card details'}`}
      body={
        showSummaryFields ? (
          <SummaryContent
            leftItems={summaryData.slice(0, Math.ceil(summaryData.length / 2))}
            rightItems={summaryData.slice(Math.ceil(summaryData.length / 2))}
          />
        ) : (
          undefined
        )
      }
      onEditClick={
        showSummaryFields
          ? () => {
              pushAnalytics('click.1.directdebitstep3.edit');
              resetFormContextError(formContextValues, formContextDispatch, 2);
              onEditClick();
            }
          : undefined
      }
    />
  );
};
